import { ColumnDef, RowData } from '@tanstack/react-table'
import { BroadcastDevice } from '@magnetar/admin-api-interfaces'
import { BroadcastDeviceVideoType } from '@magnetar/data-access'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Settings } from 'lucide-react'
import IfAnyPermission from '@/components/IfAnyPermission'
import { AllowedAction } from '@/data/allowedActions'

// 「編輯」按鈕對所有使用者開放 —— 它打開的 dialog 內含 banner / VOD /
// marquee 等子功能，clinic / clinic_group user 仍需要操作那些。基本資料
// (PUT /api/broadcast-device/:device_id) 在 dialog 內個別 disable，
// 詳見 apps/admin/src/pages/broadcastDevice/InfoSetting.tsx。
//
// 「刪除」按鈕仍限 admin + provider —— DELETE /api/broadcast-device/:device_id
// 後端用 checkAdminOrProviderClinicAccess 守住，clinic user 點下去會 403。
const BROADCAST_DEVICE_DELETE_PERMISSIONS = [
  { module: 'system_clinic', action: AllowedAction.D },
  { module: 'system_provider', action: AllowedAction.D },
  { module: 'provider_clinic_management', action: AllowedAction.D },
]

// 創建一個事件匯流排，用於跨組件通信
export type BroadcastDeviceAction = {
  type: 'edit' | 'delete'
  deviceId: number
}

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    reloadData?: () => void
    mutate?: () => void
  }
}

export type BroadcastDeviceColumnsProps = {
  onAction: (action: BroadcastDeviceAction) => void
}

export const createColumns = ({
  onAction,
}: BroadcastDeviceColumnsProps): ColumnDef<BroadcastDevice>[] => [
  {
    accessorKey: 'name',
    header: '裝置名稱',
  },
  {
    accessorKey: 'broadcast_type',
    header: '廣播類型',
    cell: ({ row }) => {
      const type: BroadcastDeviceVideoType = row.getValue('broadcast_type')
      return (
        <Badge variant="secondary">
          {type === 'tv' ? '電視' : type === 'health_vod' ? '健康影片' : type}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'is_active',
    header: '狀態',
    cell: ({ row }) => (
      <Badge variant={row.getValue('is_active') ? 'default' : 'secondary'}>
        {row.getValue('is_active') ? '啟用' : '停用'}
      </Badge>
    ),
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const broadcastDevice = row.original

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() =>
                  onAction({
                    type: 'edit',
                    deviceId: broadcastDevice.broadcast_device_id,
                  })
                }
              >
                編輯
              </DropdownMenuItem>
              <IfAnyPermission permissions={BROADCAST_DEVICE_DELETE_PERMISSIONS}>
                <DropdownMenuItem
                  className="text-destructive"
                  onSelect={() =>
                    onAction({
                      type: 'delete',
                      deviceId: broadcastDevice.broadcast_device_id,
                    })
                  }
                >
                  刪除
                </DropdownMenuItem>
              </IfAnyPermission>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )
    },
  },
]
